import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import { Alert, Box, Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material';
import StepPdfSource from 'components/@home/@documents/SentDocuments/edit-steps/StepPdfSource';
import { array, bool, func, object, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const checkboxLabelSx = {
  alignItems: 'flex-start',
  m: 0,
  '& .MuiFormControlLabel-label': { mt: 0.125 },
};

/** Icons inside read-only `Checkbox` slots — size tuned to match default small checkbox glyph. */
const summaryGlyphSx = { fontSize: 20 };

/** Read-only summary control: same look as enabled checkboxes, not interactive. */
const summaryCheckboxSx = { pointerEvents: 'none' };
const checkboxSx = { pt: '1px' };
/** Match checkbox labels: `body2` title, then `caption` + `text.secondary` (no gap before first help line). */
const summaryCaptionSx = { display: 'block', m: 0 };
const summaryCaptionStackSx = { display: 'block', m: 0, mt: 0.5 };

const SetupDocumentMeta = ({
  template,
  setTemplate,
  fields = [],
  showUploadNewEachSend = true,
  canvasPdfSrc = null,
  uploadBusy = false,
  onReplacePdf = null,
  sendSessionPdfGate = false,
}) => {
  const documentShape = template.documentShape || (template.split ? 'personalized' : 'shared');
  const isPdf = template.type === 'pdf';
  const uploadNewEachSend = template.documentSource === 'per-send';

  const items = template.items || [];
  const hasDni = items.some(i => i.name === 'dni');
  const hasWidgetFields = (fields || []).length > 0;
  const encryptChecked = template.encryptPdfWithDni === true;
  const encryptCheckboxDisabled = hasWidgetFields;
  const [encryptHintOpen, setEncryptHintOpen] = useState(false);

  useEffect(() => {
    if (!hasDni && encryptChecked) {
      setTemplate(t => ({ ...t, encryptPdfWithDni: false }));
    }
  }, [encryptChecked, hasDni, setTemplate]);

  useEffect(() => {
    if (hasWidgetFields && template.encryptPdfWithDni) {
      setTemplate(t => ({ ...t, encryptPdfWithDni: false }));
    }
  }, [hasWidgetFields, setTemplate, template.encryptPdfWithDni]);

  useEffect(() => {
    if (hasDni) setEncryptHintOpen(false);
  }, [hasDni]);

  const wantsSignature = template.isDigitalSignature === true;
  const hasRoutingItems = items.length > 0;
  const isPersonalized = documentShape === 'personalized' || template.split;

  const routingMode = useMemo(() => {
    if (!isPdf) return 'xml';
    if (isPersonalized && hasRoutingItems) return 'personalized';
    if (isPersonalized) return 'pending';
    return 'shared';
  }, [hasRoutingItems, isPdf, isPersonalized]);

  const badgeOn = I18n.t('HrBot.EditTemplate.SetupDocumentMeta.summaryBadgeOn');
  const badgeOff = I18n.t('HrBot.EditTemplate.SetupDocumentMeta.summaryBadgeOff');

  const routingSummaryTooltip =
    routingMode === 'personalized' || routingMode === 'xml' ? badgeOn : badgeOff;

  const showSplitHeadsUp = template.split && template.isDigitalSignature;
  const showEncryptNifLink = isPdf && !hasDni && !encryptCheckboxDisabled;

  const handleUploadNewEachSend = (_e, checked) => {
    setTemplate(t => ({
      ...t,
      documentSource: checked ? 'per-send' : 'fixed',
    }));
  };

  const handleEncryptChange = (_e, checked) => {
    if (checked) {
      if (!hasDni) {
        setEncryptHintOpen(true);
        return;
      }
      setTemplate(t => ({ ...t, encryptPdfWithDni: true }));
    } else {
      setTemplate(t => ({ ...t, encryptPdfWithDni: false }));
    }
  };

  const encryptDisabledTooltip = I18n.t(
    'HrBot.EditTemplate.SetupDocumentMeta.encryptDisabledHasFieldsTooltip',
  );

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        pl: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.sidebarTitle')}
      </Typography>

      {isPdf && canvasPdfSrc && onReplacePdf && (
        <StepPdfSource
          variant="compactBar"
          template={template}
          uploadBusy={uploadBusy}
          onPdfDrop={() => {}}
          canvasPdfSrc={canvasPdfSrc}
          onReplacePdf={onReplacePdf}
          copyVariant={sendSessionPdfGate ? 'sendSession' : 'editor'}
        />
      )}

      <FormControlLabel
        sx={checkboxLabelSx}
        control={
          <Tooltip title={wantsSignature ? badgeOn : badgeOff} placement="left" arrow>
            <span>
              <Checkbox
                size="small"
                disableRipple
                tabIndex={-1}
                checked={wantsSignature}
                icon={<CloseOutlinedIcon sx={{ ...summaryGlyphSx, color: 'text.secondary' }} />}
                checkedIcon={<CheckCircleIcon sx={{ ...summaryGlyphSx, color: 'success.main' }} />}
                sx={{
                  ...summaryCheckboxSx,
                  ...checkboxSx,
                  color: 'text.secondary',
                  '&.Mui-checked': { color: 'success.main' },
                }}
              />
            </span>
          </Tooltip>
        }
        label={
          <Box>
            <Typography
              variant="body2"
              component="span"
              sx={{
                display: 'block',
              }}
            >
              {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.summaryDigitalSignatureHeading')}
            </Typography>
            <Typography
              variant="caption"
              sx={[
                {
                  color: 'text.secondary',
                },
                ...(Array.isArray(summaryCaptionSx) ? summaryCaptionSx : [summaryCaptionSx]),
              ]}
            >
              {wantsSignature
                ? I18n.t('HrBot.EditTemplate.SetupDocumentMeta.summaryDigitalSignatureOnState')
                : I18n.t('HrBot.EditTemplate.SetupDocumentMeta.summaryDigitalSignatureOffAction')}
            </Typography>
          </Box>
        }
      />
      <FormControlLabel
        sx={checkboxLabelSx}
        control={
          <Tooltip title={routingSummaryTooltip} placement="left" arrow>
            <span>
              {routingMode === 'xml' && (
                <Checkbox
                  size="small"
                  disableRipple
                  tabIndex={-1}
                  checked
                  checkedIcon={
                    <AccountTreeOutlinedIcon sx={{ ...summaryGlyphSx, color: 'primary.main' }} />
                  }
                  sx={{
                    ...summaryCheckboxSx,
                    ...checkboxSx,
                    color: 'primary.main',
                    '&.Mui-checked': { color: 'primary.main' },
                  }}
                />
              )}
              {routingMode === 'personalized' && (
                <Checkbox
                  size="small"
                  disableRipple
                  tabIndex={-1}
                  checked
                  checkedIcon={
                    <CheckCircleIcon sx={{ ...summaryGlyphSx, color: 'success.main' }} />
                  }
                  sx={{
                    ...summaryCheckboxSx,
                    ...checkboxSx,
                    '&.Mui-checked': { color: 'success.main' },
                  }}
                />
              )}
              {routingMode === 'pending' && (
                <Checkbox
                  size="small"
                  disableRipple
                  tabIndex={-1}
                  checked={false}
                  indeterminate
                  indeterminateIcon={
                    <PendingOutlinedIcon sx={{ ...summaryGlyphSx, color: 'warning.main' }} />
                  }
                  sx={{
                    ...summaryCheckboxSx,
                    ...checkboxSx,
                    color: 'warning.main',
                  }}
                />
              )}
              {routingMode === 'shared' && (
                <Checkbox
                  size="small"
                  disableRipple
                  tabIndex={-1}
                  checked={false}
                  icon={<CloseOutlinedIcon sx={{ ...summaryGlyphSx, color: 'text.secondary' }} />}
                  sx={{
                    ...summaryCheckboxSx,
                    ...checkboxSx,
                    color: 'text.secondary',
                  }}
                />
              )}
            </span>
          </Tooltip>
        }
        label={
          <Box>
            <Typography
              variant="body2"
              component="span"
              sx={{
                display: 'block',
              }}
            >
              {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.summaryRoutingHeading')}
            </Typography>
            {routingMode === 'xml' && (
              <Typography
                variant="caption"
                sx={[
                  {
                    color: 'text.secondary',
                  },
                  ...(Array.isArray(summaryCaptionSx) ? summaryCaptionSx : [summaryCaptionSx]),
                ]}
              >
                {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.summaryRoutingXmlBody')}
              </Typography>
            )}
            {routingMode === 'personalized' && (
              <Typography
                variant="caption"
                sx={[
                  {
                    color: 'text.secondary',
                  },
                  ...(Array.isArray(summaryCaptionSx) ? summaryCaptionSx : [summaryCaptionSx]),
                ]}
              >
                {template.split
                  ? I18n.t(
                      'HrBot.EditTemplate.SetupDocumentMeta.summaryRoutingPersonalizedStateSplit',
                    )
                  : I18n.t(
                      'HrBot.EditTemplate.SetupDocumentMeta.summaryRoutingPersonalizedStateMultiFile',
                    )}
              </Typography>
            )}
            {routingMode === 'pending' && (
              <Typography
                variant="caption"
                sx={[
                  {
                    color: 'text.secondary',
                  },
                  ...(Array.isArray(summaryCaptionSx) ? summaryCaptionSx : [summaryCaptionSx]),
                ]}
              >
                {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.summaryRoutingPendingAction')}
              </Typography>
            )}
            {routingMode === 'shared' && (
              <Typography
                variant="caption"
                sx={[
                  {
                    color: 'text.secondary',
                  },
                  ...(Array.isArray(summaryCaptionSx) ? summaryCaptionSx : [summaryCaptionSx]),
                ]}
              >
                {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.summaryRoutingSharedAction')}
              </Typography>
            )}
            {showSplitHeadsUp && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  ...summaryCaptionStackSx,
                  fontStyle: 'italic',
                }}
              >
                {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.splitSignHeadsUp')}
              </Typography>
            )}
          </Box>
        }
      />

      {routingMode === 'personalized' && isPdf && (
        <FormControlLabel
          sx={checkboxLabelSx}
          control={
            <Checkbox
              sx={checkboxSx}
              size="small"
              checked={template.split === true}
              onChange={(_e, checked) => setTemplate(t => ({ ...t, split: checked }))}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography
                variant="body2"
                component="span"
                sx={{
                  display: 'block',
                }}
              >
                {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.splitByPageLabel')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                }}
              >
                {template.split
                  ? I18n.t('HrBot.EditTemplate.SetupDocumentMeta.splitByPageHelpOn')
                  : I18n.t('HrBot.EditTemplate.SetupDocumentMeta.splitByPageHelpOff')}
              </Typography>
            </Box>
          }
        />
      )}

      {showUploadNewEachSend && isPdf && documentShape === 'shared' && (
        <FormControlLabel
          sx={checkboxLabelSx}
          control={
            <Checkbox
              sx={checkboxSx}
              size="small"
              checked={uploadNewEachSend}
              onChange={handleUploadNewEachSend}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography
                variant="body2"
                component="span"
                sx={{
                  display: 'block',
                }}
              >
                {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.uploadNewEachSend')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                }}
              >
                {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.uploadNewEachSendHelp')}
              </Typography>
            </Box>
          }
        />
      )}

      {isPdf &&
        (encryptCheckboxDisabled ? (
          <Tooltip title={encryptDisabledTooltip} placement="left" arrow>
            <Box component="span" sx={{ display: 'block' }}>
              <FormControlLabel
                sx={{ ...checkboxLabelSx, m: 0 }}
                disabled
                control={
                  <Checkbox
                    size="small"
                    sx={checkboxSx}
                    checked={encryptChecked}
                    color="primary"
                    disabled
                  />
                }
                label={
                  <Box>
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        display: 'block',
                      }}
                    >
                      {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.encryptDocument')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                      }}
                    >
                      {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.encryptDocumentHelp')}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Tooltip>
        ) : (
          <FormControlLabel
            sx={{ ...checkboxLabelSx, m: 0 }}
            control={
              <Checkbox
                size="small"
                sx={checkboxSx}
                checked={encryptChecked}
                onChange={handleEncryptChange}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    display: 'block',
                  }}
                >
                  {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.encryptDocument')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                  }}
                >
                  {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.encryptDocumentHelp')}
                </Typography>
                {showEncryptNifLink && (
                  <Typography
                    variant="caption"
                    sx={[
                      {
                        color: 'text.secondary',
                      },
                      ...(Array.isArray(summaryCaptionStackSx)
                        ? summaryCaptionStackSx
                        : [summaryCaptionStackSx]),
                    ]}
                  >
                    {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.encryptRequiresNifCaption')}
                  </Typography>
                )}
              </Box>
            }
          />
        ))}

      {encryptHintOpen && (
        <Alert
          severity="info"
          variant="outlined"
          onClose={() => setEncryptHintOpen(false)}
          sx={{ py: 0.75 }}
        >
          {I18n.t('HrBot.EditTemplate.SetupDocumentMeta.encryptNeedDniFirst')}
        </Alert>
      )}
    </Box>
  );
};

SetupDocumentMeta.propTypes = {
  template: object.isRequired,
  setTemplate: func.isRequired,
  fields: array,
  showUploadNewEachSend: bool,
  canvasPdfSrc: string,
  uploadBusy: bool,
  onReplacePdf: func,
  sendSessionPdfGate: bool,
};

export default SetupDocumentMeta;
